import { useCallback, useState, type ReactNode } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { ClipboardCheckIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePaginatedData } from "@/hooks/usePaginatedData";
import { useReviewDirectory } from "@/components/portal/lms/useReviewDirectory";
import {
  formatUtcNaive,
  lmsReviewApi,
  type ReviewQueueItem,
  type ReviewQueueState,
} from "@/services/api/lms";

export const Route = createFileRoute("/portal/learning/review/")({
  component: ReviewQueuePage,
});

type StateFilter = "all" | ReviewQueueState;

function openLabel(item: ReviewQueueItem): string {
  return item.state === "under_review" ? "Continue" : "Review";
}

/** Loading / error / empty placeholder, or null when the table should render. */
function queueStatus(
  isLoading: boolean,
  error: string | null,
  count: number,
): ReactNode | null {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <p className="py-12 text-center text-destructive" data-testid="lms-review-queue-error">
        {error}
      </p>
    );
  }
  if (count === 0) {
    return (
      <p className="py-12 text-center text-muted-foreground" data-testid="lms-review-queue-empty">
        Nothing to review
      </p>
    );
  }
  return null;
}

const STATE_LABEL: Record<string, string> = {
  auto_graded: "Awaiting review",
  under_review: "In review",
};

/**
 * The reviewer's queue: attempts within the caller's authority that are
 * `auto_graded` or `under_review`, oldest first. Authority is resolved by dp lms
 * — a member with no grant gets an empty list (200), not an error, so this page
 * shows "Nothing to review" rather than a denial.
 */
function ReviewQueuePage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId ?? "";
  const navigate = useNavigate();
  const directory = useReviewDirectory();
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");
  const [openingId, setOpeningId] = useState<string | null>(null);

  const fetchQueue = useCallback(
    async (page: number, pageSize: number) => {
      if (!token || !organisationId) {
        return { data: [], totalCount: 0, page: 1, pageSize };
      }
      const response = await lmsReviewApi.listReviewQueue(token, {
        state: stateFilter === "all" ? undefined : stateFilter,
        page,
        pageSize,
      });
      return {
        data: response.items,
        totalCount: response.total,
        page: response.page,
        pageSize: response.page_size,
      };
    },
    [token, organisationId, stateFilter],
  );

  const {
    data: items,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<ReviewQueueItem>({
    fetchFn: fetchQueue,
    initialPageSize: 25,
    dependencies: [token, organisationId, stateFilter],
  });

  // "Review" starts the review (auto_graded → under_review; idempotent for an
  // attempt already under review) and only then opens the screen — a GET never
  // changes state, so the transition is this explicit click.
  const openReview = async (item: ReviewQueueItem) => {
    if (!token) return;
    setOpeningId(item.attempt_id);
    try {
      await lmsReviewApi.startReview(token, item.attempt_id);
      await navigate({
        to: "/portal/learning/review/$attemptId",
        params: { attemptId: item.attempt_id },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start the review");
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Review assessments | Portal</title>
      </Helmet>

      <div className="space-y-6" data-testid="lms-review-queue-page">
        <PageHeader
          title="Review assessments"
          description="Submitted tests from the teams you review. Mark each free-text answer, then finalise to release the result."
          icon={ClipboardCheckIcon}
          eyebrow="Learning"
        />

        <div className="flex items-center gap-2">
          <Select
            value={stateFilter}
            onValueChange={(v) => setStateFilter(v as StateFilter)}
          >
            <SelectTrigger className="w-48" data-testid="lms-review-state-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All pending</SelectItem>
              <SelectItem value="auto_graded">Awaiting review</SelectItem>
              <SelectItem value="under_review">In review</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {queueStatus(isLoading, error, items.length) ?? (
          <Table data-testid="lms-review-queue">
            <TableHeader>
              <TableRow>
                <TableHead>Learner</TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Test</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Auto score</TableHead>
                <TableHead className="text-right">To mark</TableHead>
                <TableHead className="w-[110px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.attempt_id} data-testid="lms-review-queue-row">
                  <TableCell title={item.learner_user_id}>
                    {directory.learnerName(item.learner_user_id)}
                  </TableCell>
                  <TableCell title={item.team_id ?? undefined}>
                    {directory.teamName(item.team_id)}
                  </TableCell>
                  <TableCell>
                    {item.test_title}
                    {item.attempt_number > 1 && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        (attempt {item.attempt_number})
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.state === "under_review" ? "secondary" : "outline"}>
                      {STATE_LABEL[item.state] ?? item.state}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatUtcNaive(item.submitted_at_utc)}</TableCell>
                  <TableCell className="text-right">
                    {item.auto_score_percent !== null
                      ? `${Number(item.auto_score_percent)}%`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right">{item.free_text_pending}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      onClick={() => openReview(item)}
                      disabled={openingId !== null}
                      data-testid="lms-review-open"
                    >
                      {openingId === item.attempt_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        openLabel(item)
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {items.length > 0 && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.pageSize}
            pageSizeOptions={pageSizeOptions}
            startIndex={startIndex}
            endIndex={endIndex}
            canGoPrev={canGoPrev}
            canGoNext={canGoNext}
            onPageChange={goToPage}
            onPageSizeChange={setPageSize}
            onFirstPage={firstPage}
            onLastPage={lastPage}
            onPrevPage={prevPage}
            onNextPage={nextPage}
          />
        )}
      </div>
    </>
  );
}
